<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { uniqueId } from 'lodash';
import AccordionIndicator from './AccordionIndicator.vue';
import InfoButton from '../UI/InfoModal/OpenButton.vue';
import InfoModal from '../UI/InfoModal/InfoModal.vue';
import MeasurementsHeightTransition from '@/components/UI/MeasurementsHeightTransition.vue';
import { StepTypes } from '@/types/step';

interface Props {
	stepName: StepTypes;
}

const props = defineProps<Props>();
const store = useStore();
const i18n = window.measurementSelectorVariables.i18n;

const step = computed(() => store.getters.getStepById(props.stepName));
const currentStepName = computed(() => store.getters.currentStepName);
const isCurrent = computed(() => currentStepName.value === props.stepName);

const htmlId = uniqueId('accordion-item-');
const variant = computed((): 'valid' | 'empty' | 'error' => {
	if (step.value.status === 'completed') {
		return 'valid';
	}
	if (step.value.status === 'invalid') {
		return 'error';
	}
	return 'empty';
});

if (!currentStepName.value) { // if no current step, set first one
	store.commit('updateCurrentStepName', props.stepName);
}

function updateCurrentStep() {
	store.commit('updateCurrentStepName', props.stepName);
}

</script>

<template>
	<section :class="['accordion-item', `accordion-item--${variant}`]" :aria-label="step.shortTitle">
		<div class="accordion-item__header">
			<div class="accordion-item__indicator">
				<accordion-indicator :variant="variant" />
			</div>
			<div>
				<button class="accordion-item__button" @click="updateCurrentStep" type="button" :aria-expanded="isCurrent" :aria-describedby="step.text && !isCurrent ? htmlId : null" :disabled="isCurrent">
					<span class="accordion-item__title">{{ step.title }}</span>
				</button>
				<measurements-height-transition :appear="false" mode="out-in">
					<p class="accordion-item__selection-info" :id="htmlId" v-if="step.text || isCurrent">
						<transition name="accordion-fade">
							<span v-show="step.text && !isCurrent">
								{{ step.status === 'invalid' ? i18n.stepError : step.text }}
							</span>
						</transition>
					</p>
				</measurements-height-transition>
			</div>
			<div v-if="step.information" class="accordion-item__info-button">
				<info-button :modal-name="stepName" :label="i18n.modalTriggerLabel"></info-button>
				<info-modal :modal-name="stepName" :content="step.information"></info-modal>
			</div>
		</div>
		<measurements-height-transition :appear="false" mode="out-in">
			<div class="accordion-item__content" :aria-hidden="!isCurrent" v-show="isCurrent">
				<slot :stepName="stepName"></slot>
			</div>
		</measurements-height-transition>
	</section>
</template>

<style scoped lang="scss">
.accordion-item {
	--accordion-item-px: 20px;
	--text-color: #{$brand-blue-dark};
	min-width: 0;
	display: flex;
	flex-direction: column;
	padding: 16px var(--accordion-item-px);
	position: relative;

	+ * {
		border-top: 1px solid $border-color;
	}

	&.accordion-item--error {
		--text-color: #{$brand-pink};
	}
}

.accordion-item__header {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: flex-start;
	gap: 20px;
}

.accordion-item__indicator {
	position: relative;
	top: 3px;
}

.accordion-item__button {
	color: var(--text-color);
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
	gap: 10px;
	padding: 0;
	width: 100%;
	position: static;
	white-space: normal;
	text-align: left;

	&:focus {
		outline: auto;
		outline-offset: 3px;
	}

	&[aria-expanded="true"] {
		cursor: default;
		text-decoration: none;

		&:focus {
			outline: none;
		}
	}

	&[disabled] {
		background-color: transparent;
	}

	&:after {
		content: '';
		position: absolute;
		inset: 0;
	}
}

.accordion-item__title {
	color: var(--text-color);
	font-weight: 600;
}

.accordion-item__info-button {
	margin-left: auto;
	position: relative;
	z-index: 2;
	flex-shrink: 0;
}

.accordion-item__content {
	display: grid;
	grid-template-rows: 1fr;
	grid-template-columns: minmax(0, 1fr);
	gap: $default-padding * 0.5;
}

.accordion-item__content-inner {
	overflow: hidden;
}

.accordion-item__selection-info {
	color: var(--text-color);
	margin: 0;
	font-size: 14px;
	line-height: 1.4;
	min-height: calc(14px * 1.4);

	span {
		display: inline-block;
	}
}

.accordion-fade-enter-active {
	transition: opacity 0.2s ease;
	transition-delay: 0.2s;
}

.accordion-fade-leave-active {
	transition: opacity 0.2s ease;
	transition-delay: 0;
}

.accordion-fade-enter-from,
.accordion-fade-leave-to {
	opacity: 0;
}
</style>
